import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

export const ciscoCatalystCenter = defineConnector({
  identifier: "ciscoCatalystCenter",
  name: "Cisco Catalyst Center",
  tagLine: "Network management, automation, and assurance",
  keywords: "Cisco DNA Center DNAC",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ host }) => {
    if (!host) return "";
    const escapedHost = host.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return `^https://${escapedHost}/dna/(?:intent|data|system)/api/`;
  },
  auth: (app) =>
    fetchTokenAuth({
      request: {
        url: `https://${app.config.host}/dna/system/api/v1/auth/token`,
        method: "POST",
        headers: [{ name: "Content-Type", value: "application/json" }],
        basicAuth: {
          username: app.config.username,
          password: app.secret.password,
        },
      },
      extract: { from: "jsonPath", paths: ["Token"] },
      apply: { kind: "header", name: "X-Auth-Token" },
      cache: { ttlSeconds: 3300 },
    }),
  steps: [
    {
      title: "Prepare your Catalyst Center account",
      items: [
        {
          kind: "markdown",
          content:
            "Use a Cisco Catalyst Center account with REST API access on your instance. This connector supports Basic authentication for obtaining tokens, not the optional AES-encrypted authentication mode or Catalyst Center Global Manager. Your instance must be reachable from 3B over HTTPS. Use a trusted server certificate, or upload your instance’s CA certificate in the customization step.",
        },
        {
          kind: "markdown",
          content:
            "The connection check reads the network-device count, so it also requires permission to read that inventory API. It works even when the count is zero and does not change any devices. A permission denial does not necessarily mean your username or password is wrong. Grant only the access needed for your workflows; a successful check does not verify access to other API operations.",
        },
        {
          kind: "markdown",
          content:
            "See the [authentication guide](https://developer.cisco.com/docs/catalyst-center/authentication) and [API documentation](https://developer.cisco.com/docs/catalyst-center/overview/). Check the API documentation for your installed release; this connector uses the `/dna/` API paths documented in release 3.1.6.",
        },
      ],
    },
    {
      title: "Enter your instance and credentials",
      items: [
        {
          kind: "hostname",
          label: "Catalyst Center host",
          required: true,
          placeholder: "catalyst-center.example.com",
          helperText:
            "Your instance’s hostname or IP address, including a port if required. Do not include https:// or an API path.",
          target: { kind: "configuration", field: "host" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ host }) => ({
      url: `https://${host}/dna/intent/api/v1/network-device/count`,
      method: "GET",
      expectedStatus: 200,
      expectedBodyContains: ['"response"', '"version"'],
    }),
  },
});
